import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Calendar, Trophy, X } from "lucide-react";
import { useTorneos, useCrearTorneo } from "../hooks/useTorneos";
import { Torneo } from "../utils/types";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

const TIPOS = ["liga", "copa", "cuadrangular", "amistoso"];

export default function TorneosPage() {
  const navigate = useNavigate();
  const { user, isReadOnly } = useAuth();
  const academiaId = user?.academia_id || "";
  const { data: torneos = [], isLoading } = useTorneos(academiaId);
  const crearTorneo = useCrearTorneo();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    nombre: "", organizador: "", tipo: "liga",
    fecha_inicio: "", fecha_fin: "", descripcion: ""
  });

  async function handleCrear(e: React.FormEvent) {
    e.preventDefault();
    try {
      await crearTorneo.mutateAsync({
        academia_id: academiaId,
        ...form,
        fecha_inicio: form.fecha_inicio || undefined,
        fecha_fin: form.fecha_fin || undefined,
      } as any);
      toast.success("Torneo creado");
      setShowForm(false);
      setForm({ nombre: "", organizador: "", tipo: "liga", fecha_inicio: "", fecha_fin: "", descripcion: "" });
    } catch {
      toast.error("Error al crear torneo");
    }
  }

  const estadoClasses = (estado: string) =>
    estado === "activo"     ? "bg-green-50 text-green-700" :
    estado === "finalizado" ? "bg-gray-100 text-gray-500"  : "bg-amber-50 text-amber-700";

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Trophy size={20} className="text-amber-500"/>
              <h1 className="text-2xl font-bold text-gray-900">Torneos</h1>
            </div>
            <p className="text-gray-500 text-sm">{torneos.length} {torneos.length === 1 ? "torneo" : "torneos"} registrados</p>
          </div>
          {!isReadOnly() && (
            <button onClick={() => setShowForm(true)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm">
              <Plus size={16}/> Nuevo torneo
            </button>
          )}
        </div>

        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
              <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <h2 className="font-bold text-gray-900 text-lg">Nuevo torneo</h2>
                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100"><X size={18}/></button>
              </div>
              <form onSubmit={handleCrear} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-1 sm:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Nombre *</label>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Liga Departamental 2025" value={form.nombre} onChange={e => setForm(f=>({...f, nombre: e.target.value}))} required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Organizador</label>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="FCF, Liga Bogotá..." value={form.organizador} onChange={e => setForm(f=>({...f, organizador: e.target.value}))} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Tipo</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.tipo} onChange={e => setForm(f=>({...f, tipo: e.target.value}))}>
                    {TIPOS.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Fecha inicio</label>
                  <input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.fecha_inicio} onChange={e => setForm(f=>({...f, fecha_inicio: e.target.value}))} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Fecha fin</label>
                  <input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.fecha_fin} onChange={e => setForm(f=>({...f, fecha_fin: e.target.value}))} />
                </div>
                <div className="col-span-1 sm:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Descripción</label>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Opcional..." value={form.descripcion} onChange={e => setForm(f=>({...f, descripcion: e.target.value}))} />
                </div>
                <div className="col-span-1 sm:col-span-2 flex gap-3 justify-end pt-2">
                  <button type="button" onClick={() => setShowForm(false)}
                    className="px-5 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium">Cancelar</button>
                  <button type="submit" disabled={crearTorneo.isPending}
                    className="px-5 py-2.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium shadow-sm">
                    {crearTorneo.isPending ? "Guardando..." : "Crear torneo"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-3">
            {[1,2,3].map(i=>(
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse flex items-center gap-4">
                <div className="w-10 h-10 bg-gray-200 rounded-lg flex-shrink-0"/>
                <div className="flex-1"><div className="h-4 bg-gray-200 rounded w-1/3 mb-2"/><div className="h-3 bg-gray-100 rounded w-1/4"/></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {torneos.map((t: Torneo) => (
              <div key={t.id}
                className="group bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between cursor-pointer hover:border-amber-300 hover:shadow-md transition-all"
                onClick={() => navigate(`/torneos/${t.id}`)}>
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Trophy size={20} className="text-amber-600"/>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{t.nombre}</h3>
                    <p className="text-sm text-gray-500">
                      {t.organizador && <span>{t.organizador} · </span>}
                      <span className="capitalize">{t.tipo ?? "torneo"}</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  {t.fecha_inicio && (
                    <span className="hidden sm:flex items-center gap-1.5">
                      <Calendar size={14}/>
                      {new Date(t.fecha_inicio).toLocaleDateString("es-CO")}
                    </span>
                  )}
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${estadoClasses(t.estado)}`}>
                    {t.estado}
                  </span>
                </div>
              </div>
            ))}
            {torneos.length === 0 && (
              <div className="text-center py-16 bg-white rounded-xl border border-dashed border-gray-300">
                <Trophy size={40} className="text-gray-300 mx-auto mb-3"/>
                <p className="text-gray-500 font-medium">Sin torneos</p>
                <p className="text-gray-400 text-sm mt-1 mb-4">Crea el primer torneo de la temporada</p>
                {!isReadOnly() && (
                  <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                    + Nuevo torneo
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

function getAcademiaId(): string {
  try {
    const token = localStorage.getItem("token");
    if (!token) return "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.academia_id ?? "";
  } catch {
    return "";
  }
}

